import React, { useState, useEffect, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import Header from '../common/Header'
import { Card, CardTitle } from '../UI'
import { X, Eye, EyeOff } from 'lucide-react'
import { deleteAuthToken, notifySuccess, notifyError } from '../../utils/helper'
import { removeUserInformation, setUserInformation } from '../../_store/reducers/userReducer'
import { changePassword, getUserInfo, updateAdmin } from '../../requests'
import { startLoader, stopLoader } from '../../_store/reducers/loaderReducer'
import styles from '../../_css/Profile.module.css'
import { validateName } from '../helper/validators'
import useRole from '../../hooks/getRole'
import { clearContext } from '../../_store/reducers/contextReducer'

const Index = () => {
  const role = useRole()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const userInfo = useSelector((state) => state.user)
  const [profileData, setProfileData] = useState(null)
  const [fullName, setFullName] = useState(userInfo?.fullName || '')
  const [email, setEmail] = useState(userInfo?.email || '')
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    setFullName(userInfo?.fullName || '')
    setEmail(userInfo?.email || '')
  }, [userInfo?.fullName, userInfo?.email])

  useEffect(() => {
    if (profileData) {
      setFullName(profileData.full_name || '')
      setEmail(profileData.email || '')
    }
  }, [profileData])

  const isSuperAdmin = userInfo?.isSuperAdmin == true || userInfo?.isSuperAdmin == 'true'

  const displayName = profileData?.full_name ?? userInfo?.fullName ?? '—'
  const initials = displayName !== '—' ? displayName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase() : '?'
  const agency = profileData?.agency

  const handleSavePersonalInfo = async () => {
    const name = (fullName || '').trim()
    if (!name) {
      notifyError('Please enter full name')
      return
    }
    if (name.length < 3) {
      notifyError('Please enter a valid full name with at least 3 characters')
      return
    }
    if(!validateName(name)) {
      notifyError('Please enter a valid full name without special characters or numbers')
      return
    }
    const userId = profileData?.id ?? userInfo?.userId
    if (!userId) {
      notifyError('Unable to update: user ID missing')
      return
    }
    dispatch(startLoader())
    try {
      await updateAdmin({ id: userId, full_name: name, role: userInfo.role})
      dispatch(setUserInformation(
        name,
        userId,
        userInfo.email,
        userInfo.status,
        userInfo.isSuperAdmin,
        userInfo.agencyId,
        userInfo.invitedById,
        userInfo.agency_name || '',
        userInfo.agency_email || '',
        userInfo.role
      ))
      await getUserInfoData()
      notifySuccess('Profile updated successfully')
    } catch (err) {
      notifyError(err?.response?.data?.message || err?.message || 'Failed to update profile')
    } finally {
      dispatch(stopLoader())
    }
  }

  const handleUpdatePassword = async () => {
    if (!currentPassword || !newPassword) {
      notifyError('Please enter both current and new password')
      return
    }
    if (newPassword.length < 8) {
      notifyError('New password must be at least 8 characters')
      return
    }
    setLoading(true)
    try {
      const response = await changePassword({ oldPassword: currentPassword, newPassword: newPassword })
      if (response?.status < 400) {
        notifySuccess('Password updated successfully')
        setCurrentPassword('')
        setNewPassword('')
      }
      
    } catch (error) {
      notifyError(error?.response?.data?.message || error?.message || 'Failed to update password')
    } finally {
      setLoading(false)
    }





  }

  const handleLogout = () => {
    deleteAuthToken()
    dispatch(removeUserInformation())
    dispatch(clearContext())
    navigate('/login')
    notifySuccess('Logged out successfully')
  }

  const getUserInfoData = useCallback(async () => {
    try {
      const response = await getUserInfo({ isSuperAdmin: false })
      if (response?.status < 400 && response?.data) {
        setProfileData(response.data)
      }
    } catch (error) {
      console.log(error)
    }
  }, [userInfo?.agencyId])

  const full_name_disabled = role === 'user' ? true : role === 'super_admin' ? true : false

  const personalFields = [
    { l: 'Full Name', v: fullName, set: setFullName, disabled: full_name_disabled },
    { l: 'Email Address', v: email, set: setEmail, disabled: true },
    { l: 'Role', v: role === 'super_admin' ? 'Super Admin' : role === 'admin' ? 'Agency Admin' : 'User', disabled: true },
  ]

  const agencyData = [
    { l: 'Agency', v: agency?.name ?? userInfo?.agency_name ?? '—' },
    { l: 'Agency Email', v: agency?.email ?? userInfo?.agency_email ?? '—' },
    { l: 'Address', v: agency?.address ?? '—' },
    { l: 'Active Members', v: agency?._count?.users != null ? String(agency._count.users) : '—' },
  ]


  useEffect(() => {
    getUserInfoData();
  }, [getUserInfoData]);

  return (
    <div>
      {/* <Header userType={role} /> */}
      <div className={styles.page}>
        <button
          type="button"
          onClick={() => navigate(-1)}
          className={styles.closeBtn}
          aria-label="Close"
        >
          <X size={20} />
        </button>

        <div className={styles.header}>
          <h1 className={styles.title}>Profile</h1>
          <p className={styles.subtitle}>Manage your account settings</p>
        </div>

        <Card className={styles.personalCard}>
          <CardTitle>Personal Information</CardTitle>
          <div className={styles.profileRow}>
            <div className={styles.avatar}>{initials}</div>
            <div>
              <div className={styles.userName}>{displayName}</div>
              <div className={styles.userMeta}>
                {role === 'super_admin' ? 'Super Admin' : role === 'admin' ? 'Agency Admin' : 'User'} · {agency?.name ?? userInfo?.agency_name ?? '—'}
              </div>
            </div>
          </div>
          <div className={styles.fieldsGrid}>
            {personalFields.map((f, i) => (
              <div key={i} className={styles.fieldWrap}>
                <label className={styles.label}>{f.l}</label>
                <input
                  value={f.v}
                  onChange={f.disabled ? undefined : (e) => f.set(e.target.value)}
                  disabled={f.disabled}
                  className={styles.input}
                />
              </div>
            ))}
          </div>
          { !full_name_disabled && <button
            type="button"
            onClick={handleSavePersonalInfo}
            className={styles.saveBtn}
          >
            Save Changes
          </button>}
        </Card>

        <Card className={styles.agencyCard}>
          <CardTitle>Agency</CardTitle>
          <div className={styles.agencyGrid}>
            {agencyData.map((d, i) => (
              <div key={i} className={styles.agencyItem}>
                <div className={styles.agencyLabel}>{d.l}</div>
                <div className={styles.agencyValue}>{d.v}</div>
              </div>
            ))}
          </div>
        </Card>

        {role !== 'super_admin' && (
          <Card className={styles.securityCard}>
            <CardTitle>Security</CardTitle>
            <div className={styles.securityGrid}>
              <div className={styles.securityField}>
                <label className={styles.label} htmlFor="profile-current-password">
                  Current Password
                </label>
                <div className={styles.passwordField}>
                  <input
                    id="profile-current-password"
                    type={showCurrentPassword ? 'text' : 'password'}
                    placeholder="Enter current password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    className={styles.passwordInput}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword((p) => !p)}
                    className={styles.passwordToggle}
                    aria-label={showCurrentPassword ? 'Hide current password' : 'Show current password'}
                  >
                    {showCurrentPassword ? <Eye size={18} /> : <EyeOff size={18} />}
                  </button>
                </div>
              </div>
              <div className={styles.securityField}>
                <label className={styles.label} htmlFor="profile-new-password">
                  New Password
                </label>
                <div className={styles.passwordField}>
                  <input
                    id="profile-new-password"
                    type={showNewPassword ? 'text' : 'password'}
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className={styles.passwordInput}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword((p) => !p)}
                    className={styles.passwordToggle}
                    aria-label={showNewPassword ? 'Hide new password' : 'Show new password'}
                  >
                    {showNewPassword ? <Eye size={18} /> : <EyeOff size={18} />}
                  </button>
                </div>
              </div>
            </div>
            <button
              disabled={loading}
              type="button"
              onClick={handleUpdatePassword}
              className={styles.updatePasswordBtn}
            >
              {loading ? 'Updating...' : 'Update Password'}
            </button>
          </Card>
        )}

        <button
          type="button"
          onClick={handleLogout}
          className={styles.logoutBtn}
        >
          Logout
        </button>
      </div>
    </div>
  )
}

export default Index
